import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IoCheckmarkCircle,
  IoCloudDownloadOutline,
  IoOpenOutline,
  IoSearchOutline,
  IoTrashOutline,
  IoImagesOutline,
} from "react-icons/io5";
import Menu from "components/common/Menu";
import { useTabActive } from "hook/useTabActive";
import { SideBarTabs } from "constants/tabs";
import { Paths } from "router/Paths";
import { getCarMakes } from "api/services/dealer";
import {
  ExternalImportPreview,
  ExternalImportProvider,
  GariImportJobStatus,
  getGariImportJob,
  previewGariImport,
  previewMekinaImport,
  previewFacebookImport,
  createFacebookImportJob,
  getFacebookImportJob,
  publishGariImport,
  publishMekinaImport,
  publishFacebookImport,
} from "api/services/externalImports";
import { CarMakeType } from "types/loadedData";

const formatPrice = (value?: string | number) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits ? Number(digits).toLocaleString("en-US") : "";
};

const errorMessage = (error: any, fallback: string) =>
  error?.response?.data?.msg ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

function CarImportPage() {
  useTabActive(SideBarTabs.CarImport.name);
  const navigate = useNavigate();
  const [provider, setProvider] = useState<ExternalImportProvider>("Mekina");
  const [facebookCaption, setFacebookCaption] = useState("");
  const [facebookMode, setFacebookMode] = useState<"automatic" | "manual">("automatic");
  const [facebookJobId, setFacebookJobId] = useState("");
  const [facebookJobStatus, setFacebookJobStatus] = useState<GariImportJobStatus | "">("");
  const [facebookPhotos, setFacebookPhotos] = useState<{ file: File; url: string }[]>([]);
  const photoRef = useRef(facebookPhotos);
  const [sourceUrl, setSourceUrl] = useState("");
  const [gariJobId, setGariJobId] = useState("");
  const [gariJobStatus, setGariJobStatus] = useState<GariImportJobStatus | "">("");
  const [listing, setListing] = useState<ExternalImportPreview | null>(null);
  const [makes, setMakes] = useState<CarMakeType[]>([]);
  const [images, setImages] = useState<string[]>([]);
  const [sellerName, setSellerName] = useState("");
  const [sellerPhone, setSellerPhone] = useState("");
  const [rightsConfirmed, setRightsConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [form, setForm] = useState({
    make: "",
    model: "",
    year: "",
    price: "",
    fuelType: "",
    transmissionType: "",
    color: "",
    location: "Addis Ababa",
    mileage: "",
    usageType: "used" as "new" | "used",
    listingType: "Sale" as "Sale" | "Rent",
    description: "",
  });

  useEffect(() => {
    getCarMakes().then(setMakes).catch(() => setMessage("Car brands could not be loaded."));
  }, []);

  useEffect(() => () => {
    photoRef.current.forEach((photo) => URL.revokeObjectURL(photo.url));
  }, []);

  const clearFacebookPhotos = () => {
    photoRef.current.forEach((photo) => URL.revokeObjectURL(photo.url));
    photoRef.current = [];
    setFacebookPhotos([]);
  };

  const addFacebookPhotos = (files: FileList | null) => {
    if (!files) return;
    const incoming = Array.from(files);
    if (incoming.some((file) => !/\.(?:jpe?g|jfif|png|webp|heic|heif|avif)$/i.test(file.name) || file.size > 10 * 1024 * 1024)) {
      setMessage("Choose JPG, PNG, WEBP, HEIC, HEIF, or AVIF photos, up to 10 MB each.");
      return;
    }
    const unique = incoming.filter((file) => !facebookPhotos.some((photo) =>
      photo.file.name === file.name && photo.file.size === file.size && photo.file.lastModified === file.lastModified));
    if (facebookPhotos.length + unique.length > 10) {
      setMessage("Choose no more than 10 photos.");
      return;
    }
    const next = [...facebookPhotos, ...unique.map((file) => ({ file, url: URL.createObjectURL(file) }))];
    photoRef.current = next;
    setFacebookPhotos(next);
    setImages(next.map((photo) => photo.url));
    setMessage("");
  };

  const removePhoto = (image: string) => {
    setImages((current) => current.filter((item) => item !== image));
    if (provider === "Facebook") {
      const next = facebookPhotos.filter((photo) => photo.url !== image);
      URL.revokeObjectURL(image);
      photoRef.current = next;
      setFacebookPhotos(next);
    }
  };

  const canPublish = useMemo(
    () =>
      Boolean(
        listing &&
          !listing.duplicateCar &&
          form.make &&
          form.model.trim() &&
          form.year &&
          form.price.replace(/\D/g, "") &&
          images.length &&
          images.length <= 10 &&
          (provider !== "Facebook" || facebookMode === "manual" || facebookJobId) &&
          (provider !== "Facebook" || form.location.trim()) &&
          (provider !== "Gari" || gariJobId) &&
          rightsConfirmed
      ),
    [listing, form, provider, images.length, gariJobId, rightsConfirmed, facebookMode, facebookJobId]
  );

  const applyPreview = useCallback((preview: ExternalImportPreview) => {
    setListing(preview);
    setImages(provider === "Facebook" && facebookMode === "manual" ? facebookPhotos.map((photo) => photo.url)
      : preview.images.length > 10 ? [] : preview.images);
    setSellerName(preview.sellerName || "");
    setSellerPhone(preview.sellerPhone || "");
    setRightsConfirmed(false);
    setForm({
      make: preview.matchedMake?._id || "",
      model: preview.model || "",
      year: String(preview.year || ""),
      price: formatPrice(preview.price),
      fuelType: preview.fuelType || "",
      transmissionType: preview.transmissionType || "",
      color: preview.color || "",
      location: preview.location || (provider === "Facebook" ? "" : "Addis Ababa"),
      mileage: String(preview.mileage ?? ""),
      usageType: preview.usageType === "new" ? "new" : "used",
      listingType: preview.listingType === "Rent" ? "Rent" : "Sale",
      description: preview.description || "",
    });
  }, [provider, facebookPhotos, facebookMode]);

  useEffect(() => {
    if (!facebookJobId || provider !== "Facebook" || facebookMode !== "automatic" || listing) return;
    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const deadline = Date.now() + 5 * 60 * 1000;
    const poll = async () => {
      try {
        const job = await getFacebookImportJob(facebookJobId);
        if (cancelled) return;
        setFacebookJobStatus(job.status);
        if (job.status === "Ready" && job.listing) {
          applyPreview(job.listing);
          setMessage("");
          return;
        }
        if (job.status === "Failed") {
          setMessage(job.error || "The Facebook listing could not be extracted.");
          return;
        }
        if (Date.now() >= deadline) {
          setFacebookJobStatus("Failed");
          setMessage("Start the Facebook import connector on your computer, then retry. It must stay open while extracting.");
          return;
        }
        timeout = setTimeout(poll, 2500);
      } catch (error: any) {
        if (!cancelled) {
          setFacebookJobStatus("Failed");
          setMessage(errorMessage(error, "Facebook extraction status could not be loaded. Retry extraction."));
        }
      }
    };
    void poll();
    return () => { cancelled = true; if (timeout) clearTimeout(timeout); };
  }, [facebookJobId, provider, facebookMode, listing, applyPreview]);

  useEffect(() => {
    if (!gariJobId || provider !== "Gari" || listing) return;
    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      try {
        const job = await getGariImportJob(gariJobId);
        if (cancelled) return;
        setGariJobStatus(job.status);
        if (job.status === "Ready" && job.listing) {
          applyPreview(job.listing);
          setMessage("");
          return;
        }
        if (job.status === "Failed") {
          setMessage(job.error || "The Gari listing could not be extracted.");
          return;
        }
        timeout = setTimeout(poll, 2000);
      } catch (error: any) {
        if (!cancelled) {
          setMessage(errorMessage(error, "Gari extraction status could not be loaded."));
        }
      }
    };

    void poll();
    return () => {
      cancelled = true;
      if (timeout) clearTimeout(timeout);
    };
  }, [gariJobId, provider, listing, applyPreview]);

  const preview = async () => {
    if (!sourceUrl.trim()) {
      setMessage(`Paste a ${provider} shared listing URL first.`);
      return;
    }
    if (provider === "Facebook" && facebookMode === "manual" && !facebookCaption.trim()) {
      setMessage("Paste the Facebook post description first.");
      return;
    }
    setLoading(true);
    setMessage("");
    if (provider === "Facebook") {
      setListing(null);
      setImages([]);
      setRightsConfirmed(false);
      setFacebookJobId("");
      setFacebookJobStatus("");
    }
    if (provider === "Gari") {
      setListing(null);
      setImages([]);
      setRightsConfirmed(false);
      setGariJobId("");
      setGariJobStatus("");
    }
    try {
      const previewData =
        provider === "Mekina"
          ? await previewMekinaImport(sourceUrl.trim())
          : provider === "Facebook" && facebookMode === "manual"
          ? await previewFacebookImport(sourceUrl.trim(), facebookCaption)
          : null;
      if (previewData) {
        applyPreview(previewData);
      } else {
        if (provider === "Facebook") {
          const job = await createFacebookImportJob(sourceUrl.trim());
          setFacebookJobId(job._id);
          setFacebookJobStatus(job.status);
        } else {
          const job = await previewGariImport(sourceUrl.trim());
          setGariJobId(job._id);
          setGariJobStatus(job.status);
        }
      }
    } catch (error: any) {
      setListing(null);
      setMessage(errorMessage(error, `The ${provider} listing could not be prepared.`));
    } finally {
      setLoading(false);
    }
  };

  const publish = async () => {
    if (!listing || !canPublish) return;
    setLoading(true);
    setMessage("");
    try {
      const common = {
        sourceUrl: listing.sourceUrl,
        ...form,
        price: form.price.replace(/,/g, ""),
        rightsConfirmed,
      };
      const car =
        provider === "Mekina"
          ? await publishMekinaImport({ ...common, images })
          : provider === "Facebook"
          ? await publishFacebookImport({ ...common, sellerName, sellerPhone,
              ...(facebookMode === "automatic" ? { jobId: facebookJobId, selectedImages: images } : {}) },
              facebookMode === "manual" ? facebookPhotos.map((photo) => photo.file) : [])
          : await publishGariImport({
              ...common,
              jobId: gariJobId,
              images,
              sellerName,
              sellerPhone,
            });
      navigate(Paths.car.replace(":id", String(car._id)));
    } catch (error: any) {
      setMessage(errorMessage(error, "The listing could not be imported."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={ClassName.page}>
      <Menu />
      <header className={ClassName.header}>
        <div>
          <span className={ClassName.eyebrow}>CONTROLLED SOURCE IMPORT</span>
          <h1 className={ClassName.heading}>Import an external car listing</h1>
          <p className={ClassName.subheading}>
            Select the source, review every detail, then publish it to BossCar.
          </p>
        </div>
      </header>

      <main className={ClassName.main}>
        <section className={ClassName.urlCard}>
          <div className={ClassName.providerTabs}>
            {(["Mekina", "Gari", "Facebook"] as const).map((item) => (
              <button
                type="button"
                key={item}
                disabled={loading}
                className={provider === item ? ClassName.providerActive : ClassName.providerTab}
                onClick={() => {
                  setProvider(item);
                  setSourceUrl("");
                  setGariJobId("");
                  setGariJobStatus("");
                  setFacebookJobId("");
                  setFacebookJobStatus("");
                  setListing(null);
                  setImages([]);
                  setFacebookCaption("");
                  clearFacebookPhotos();
                  setRightsConfirmed(false);
                  setMessage("");
                }}
              >
                {item}
              </button>
            ))}
          </div>
          <label className={ClassName.label} htmlFor="source-url">
            {provider} shared listing URL
          </label>
          <div className={ClassName.urlRow}>
            <IoSearchOutline size={21} className="text-slate-400" />
            <input
              id="source-url"
              value={sourceUrl}
              disabled={loading}
              onChange={(event) => {
                setSourceUrl(event.target.value);
                if (provider === "Gari" || provider === "Facebook" || listing) {
                  setGariJobId("");
                  setGariJobStatus("");
                  setFacebookJobId("");
                  setFacebookJobStatus("");
                  setListing(null);
                  setImages([]);
                  if (provider === "Facebook") {
                    clearFacebookPhotos();
                    setFacebookCaption("");
                  }
                  setRightsConfirmed(false);
                }
              }}
              onKeyDown={(event) => event.key === "Enter" && void preview()}
              placeholder={
                provider === "Mekina"
                  ? "https://www.mekina.net/cars/..."
                  : provider === "Facebook"
                  ? "https://www.facebook.com/marketplace/item/... or a post link"
                  : "https://gari-ethiopia.web.app/adv/..."
              }
              className={ClassName.urlInput}
            />
            <button
              type="button"
              className={ClassName.previewButton}
              onClick={preview}
              disabled={loading}
            >
              <IoCloudDownloadOutline size={19} />
              {loading && !listing ? "Preparing..." : provider === "Gari" || (provider === "Facebook" && facebookMode === "automatic") ? "Extract" : "Preview"}
            </button>
          </div>
          <p className={ClassName.helper}>
            {provider === "Mekina"
              ? "New/Used comes from Mekina's RSS category. Always confirm it before publishing."
              : provider === "Facebook"
              ? "Paste the listing link. Keep the Facebook import connector open on your computer; details and photos will appear here for review."
              : "Paste only the Gari URL. The authorized BossCar connector extracts the listing details and original photos for review."}
          </p>
          {provider === "Facebook" ? <div className="mt-3 flex items-center gap-3 text-xs">
            <button type="button" disabled={loading} className="font-semibold underline underline-offset-4"
              onClick={() => {
                setFacebookMode(facebookMode === "automatic" ? "manual" : "automatic");
                setFacebookJobId(""); setFacebookJobStatus(""); setListing(null); setImages([]);
                clearFacebookPhotos(); setRightsConfirmed(false); setMessage("");
              }}>{facebookMode === "automatic" ? "Add details manually instead" : "Extract from link instead"}</button>
          </div> : null}
          {provider === "Facebook" && facebookMode === "manual" ? (
            <div className="mt-4">
              <label className={ClassName.label} htmlFor="facebook-caption">Post description</label>
              <textarea id="facebook-caption" value={facebookCaption} disabled={loading}
                placeholder="Paste the car details from Facebook"
                onChange={(event) => {
                  setFacebookCaption(event.target.value);
                  setListing(null);
                  setRightsConfirmed(false);
                }} className={ClassName.textarea} maxLength={12000} />
              <label className={`${ClassName.imagePicker} mt-3`}>
                <IoImagesOutline size={19} /> Add photos {facebookPhotos.length ? `(${facebookPhotos.length}/10)` : ""}
                <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/avif,.jpg,.jpeg,.jfif,.png,.webp,.heic,.heif,.avif" multiple disabled={loading}
                  onChange={(event) => { addFacebookPhotos(event.target.files); event.target.value = ""; }} />
              </label>
              {!listing && facebookPhotos.length ? <div className={`${ClassName.photoGrid} mt-4`}>
                {facebookPhotos.map((photo, index) => <div className={ClassName.photo} key={photo.url}>
                  <img src={photo.url} alt={`Facebook car ${index + 1}`} />
                  {index === 0 ? <span>Primary</span> : null}
                  <button type="button" disabled={loading} onClick={() => removePhoto(photo.url)} aria-label={`Remove photo ${index + 1}`}><IoTrashOutline size={18} /></button>
                </div>)}
              </div> : null}
            </div>
          ) : null}
          {provider === "Facebook" && facebookMode === "automatic" && facebookJobStatus && !listing ? (
            <div className={ClassName.connectorStatus} role="status">
              {facebookJobStatus !== "Failed" ? <span className={ClassName.statusDot} /> : null}
              {facebookJobStatus === "Pending" ? "Waiting for your Facebook import connector. Open Start-FacebookImport on your computer."
                : facebookJobStatus === "Processing" ? "Reading the Facebook listing and its photos. Check the connector browser if Facebook asks you to sign in."
                : "Extraction stopped. Retry after checking the connector browser."}
            </div>
          ) : null}
          {provider === "Gari" && gariJobStatus && !listing ? (
            <div className={ClassName.connectorStatus}>
              <span className={ClassName.statusDot} />
              {gariJobStatus === "Pending"
                ? "Waiting for the BossCar Gari connector..."
                : gariJobStatus === "Processing"
                ? "Extracting the Gari listing and photos..."
                : gariJobStatus === "Failed"
                ? "Extraction failed"
                : "Preparing the review..."}
            </div>
          ) : null}
        </section>

        {message ? <div className={ClassName.message}>{message}</div> : null}

        {listing ? (
          <>
            <section className={ClassName.sourceCard}>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={ClassName.sourceBadge}>{listing.sourceCategory || provider}</span>
                  <span className={ClassName.muted}>{listing.classificationSource}</span>
                </div>
                <h2 className={ClassName.sourceTitle}>{listing.title}</h2>
                <p className={ClassName.muted}>
                  Source seller: {listing.sellerName || "Not entered"}
                  {listing.sellerPhone ? ` / ${listing.sellerPhone}` : ""}
                </p>
              </div>
              <a
                href={listing.sourceUrl}
                target="_blank"
                rel="noreferrer"
                className={ClassName.sourceLink}
              >
                Open source <IoOpenOutline />
              </a>
            </section>

            {listing.duplicateCar ? (
              <section className={ClassName.duplicateCard}>
                <IoCheckmarkCircle size={24} />
                <div className="flex-1">
                  <strong>Already imported</strong>
                  <p className="text-sm">This source URL is already connected to a BossCar post.</p>
                </div>
                <button
                  type="button"
                  className={ClassName.openCarButton}
                  onClick={() =>
                    navigate(Paths.car.replace(":id", String(listing.duplicateCar?._id)))
                  }
                >
                  Open car
                </button>
              </section>
            ) : null}

            <section className={ClassName.editorCard}>
              <div className={ClassName.sectionHeading}>
                <div>
                  <h2 className="text-lg font-bold">Review listing details</h2>
                  <p className={ClassName.helper}>Only these reviewed values will appear in BossCar.</p>
                </div>
                <div className={ClassName.segmented}>
                  {(["new", "used"] as const).map((value) => (
                    <button
                      type="button"
                      key={value}
                      className={form.usageType === value ? ClassName.segmentActive : ClassName.segment}
                      onClick={() => setForm({ ...form, usageType: value })}
                    >
                      {value === "new" ? "New" : "Used"}
                    </button>
                  ))}
                </div>
              </div>

              <div className={ClassName.formGrid}>
                <label>Brand<select value={form.make} onChange={(event) => setForm({ ...form, make: event.target.value })} className={ClassName.input}><option value="">Select brand</option>{makes.map((make) => <option key={make._id} value={make._id}>{make.name}</option>)}</select></label>
                <label>Model<input value={form.model} onChange={(event) => setForm({ ...form, model: event.target.value })} className={ClassName.input} /></label>
                <label>Year<input inputMode="numeric" value={form.year} onChange={(event) => setForm({ ...form, year: event.target.value.replace(/\D/g, "").slice(0, 4) })} className={ClassName.input} /></label>
                <label>Price (Birr)<input inputMode="numeric" value={form.price} onChange={(event) => setForm({ ...form, price: formatPrice(event.target.value) })} className={ClassName.input} /></label>
                <label>Fuel<input value={form.fuelType} onChange={(event) => setForm({ ...form, fuelType: event.target.value })} className={ClassName.input} /></label>
                <label>Transmission<input value={form.transmissionType} onChange={(event) => setForm({ ...form, transmissionType: event.target.value })} className={ClassName.input} /></label>
                <label>Color<input value={form.color} onChange={(event) => setForm({ ...form, color: event.target.value })} className={ClassName.input} /></label>
                <label>Mileage<input inputMode="numeric" value={form.mileage} onChange={(event) => setForm({ ...form, mileage: event.target.value.replace(/\D/g, "") })} className={ClassName.input} /></label>
                <label>Location<input value={form.location} onChange={(event) => setForm({ ...form, location: event.target.value })} className={ClassName.input} /></label>
                <label>Listing<select value={form.listingType} onChange={(event) => setForm({ ...form, listingType: event.target.value as "Sale" | "Rent" })} className={ClassName.input}><option value="Sale">Sale</option><option value="Rent">Rent</option></select></label>
                {provider !== "Mekina" ? <label>Source seller (private)<input value={sellerName} onChange={(event) => setSellerName(event.target.value)} className={ClassName.input} /></label> : null}
                {provider !== "Mekina" ? <label>Source phone (private)<input inputMode="tel" value={sellerPhone} onChange={(event) => setSellerPhone(event.target.value)} className={ClassName.input} /></label> : null}
              </div>

              <label className="mt-4 block text-sm font-semibold">Description<textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} className={ClassName.textarea} /></label>
            </section>

            <section className={ClassName.editorCard}>
              <div className={ClassName.sectionHeading}>
                <div>
                  <h2 className="text-lg font-bold">Photos</h2>
                   <p className={ClassName.helper}>{images.length} selected / at least 1 required / maximum 10
                     {provider === "Facebook" && facebookMode === "automatic" ? ` · ${listing.images.length} found. Tap photos to select; the first selection is the cover.` : ""}</p>
                 </div>
               </div>
               <div className={ClassName.photoGrid}>
                 {(provider === "Facebook" && facebookMode === "automatic" ? listing.images : images).map((image, index) => (
                   <div className={ClassName.photo} key={image}>
                     <img src={image} alt={`Source car ${index + 1}`} />
                     {images[0] === image ? <span>Primary</span> : null}
                     {provider === "Facebook" && facebookMode === "automatic" ? (
                       <button type="button" disabled={loading || (!images.includes(image) && images.length >= 10)}
                         aria-label={`${images.includes(image) ? "Deselect" : "Select"} photo ${index + 1}`} aria-pressed={images.includes(image)}
                         onClick={() => setImages((current) => current.includes(image) ? current.filter((item) => item !== image) : [...current, image])}>
                         {images.includes(image) ? <IoCheckmarkCircle size={23} /> : <span className="h-4 w-4 rounded-full border-2 border-white" />}
                       </button>
                     ) : <button type="button" disabled={loading} onClick={() => removePhoto(image)} aria-label="Remove photo"><IoTrashOutline size={18} /></button>}
                   </div>
                 ))}
              </div>
            </section>

            <section className={ClassName.publishCard}>
              <label className={ClassName.confirmRow}>
                <input type="checkbox" checked={rightsConfirmed} onChange={(event) => setRightsConfirmed(event.target.checked)} />
                <span>I confirm BossCar has permission to republish this listing and its photos.</span>
              </label>
              <button type="button" className={ClassName.publishButton} disabled={!canPublish || loading} onClick={publish}>
                {loading ? "Publishing..." : "Publish to BossCar"}
              </button>
            </section>
          </>
        ) : null}
      </main>
    </div>
  );
}

export default CarImportPage;

const ClassName = {
  page: "min-h-screen flex-1 min-w-0 bg-slate-100 pb-24 text-slate-950",
  header: "border-b border-slate-200 bg-white px-5 pb-6 pt-20 sm:px-8 xl:pt-8",
  eyebrow: "text-[11px] font-bold tracking-[0.2em] text-primary",
  heading: "mt-2 text-2xl font-bold sm:text-3xl",
  subheading: "mt-2 max-w-2xl text-sm text-slate-500",
  main: "mx-auto flex w-full max-w-6xl flex-col gap-4 p-4 sm:p-8",
  urlCard: "rounded-2xl border border-slate-200 bg-white p-4 sm:p-6",
  providerTabs: "mb-5 grid max-w-sm grid-cols-3 rounded-xl bg-slate-100 p-1",
  providerTab: "h-10 rounded-lg text-sm font-semibold text-slate-500",
  providerActive: "h-10 rounded-lg bg-white text-sm font-bold text-slate-950 shadow-sm",
  label: "mb-2 block text-sm font-semibold",
  urlRow: "flex min-h-14 items-center gap-3 rounded-xl border border-slate-300 bg-white px-4 focus-within:border-slate-900",
  urlInput: "min-w-0 flex-1 bg-transparent text-sm outline-none",
  previewButton: "flex h-10 items-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white disabled:opacity-50",
  helper: "mt-2 text-xs leading-5 text-slate-500",
  connectorStatus: "mt-4 flex items-center gap-2 rounded-xl bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-700",
  statusDot: "h-2.5 w-2.5 animate-pulse rounded-full bg-primary",
  message: "rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700",
  sourceCard: "flex flex-col justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-5 sm:flex-row sm:items-center",
  sourceBadge: "rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-primary",
  sourceTitle: "mt-3 text-xl font-bold",
  muted: "text-xs text-slate-500",
  sourceLink: "inline-flex shrink-0 items-center gap-2 text-sm font-semibold text-slate-700 hover:text-primary",
  duplicateCard: "flex flex-col items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-800 sm:flex-row sm:items-center",
  openCarButton: "rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white",
  editorCard: "rounded-2xl border border-slate-200 bg-white p-4 sm:p-6",
  sectionHeading: "mb-4 flex flex-col justify-between gap-3 sm:flex-row sm:items-center",
  imagePicker: "inline-flex h-10 cursor-pointer items-center gap-2 rounded-lg border border-slate-300 px-4 text-sm font-semibold text-slate-700 hover:border-slate-900 [&>input]:hidden",
  segmented: "grid grid-cols-2 rounded-xl bg-slate-100 p-1",
  segment: "h-9 rounded-lg px-5 text-sm font-semibold text-slate-500",
  segmentActive: "h-9 rounded-lg bg-white px-5 text-sm font-semibold text-slate-950 shadow-sm",
  formGrid: "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 [&>label]:text-sm [&>label]:font-semibold",
  input: "mt-2 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 font-normal outline-none focus:border-slate-900",
  textarea: "mt-2 min-h-32 w-full resize-y rounded-xl border border-slate-200 p-3 font-normal outline-none focus:border-slate-900",
  photoGrid: "grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4",
  photo: "relative overflow-hidden rounded-xl bg-slate-100 [&>img]:aspect-[4/3] [&>img]:h-full [&>img]:w-full [&>img]:object-cover [&>span]:absolute [&>span]:left-2 [&>span]:top-2 [&>span]:rounded-full [&>span]:bg-white [&>span]:px-2 [&>span]:py-1 [&>span]:text-[10px] [&>span]:font-bold [&>button]:absolute [&>button]:right-2 [&>button]:top-2 [&>button]:flex [&>button]:h-9 [&>button]:w-9 [&>button]:items-center [&>button]:justify-center [&>button]:rounded-full [&>button]:bg-black/75 [&>button]:text-white",
  publishCard: "sticky bottom-3 z-20 flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-xl backdrop-blur sm:flex-row sm:items-center sm:justify-between",
  confirmRow: "flex max-w-2xl items-start gap-3 text-sm text-slate-600 [&>input]:mt-1 [&>input]:h-4 [&>input]:w-4",
  publishButton: "h-12 shrink-0 rounded-xl bg-primary px-7 font-bold text-white disabled:cursor-not-allowed disabled:opacity-40",
};

